// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
import { Fragment, Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FacilitatorScopeProvider } from '../hooks/useFacilitatorScope';
import AppShell, {
  ENGAGEMENT_ACTIVE_NAVIGATION,
  ENGAGEMENT_DRAFT_NAVIGATION,
  MODERNISATION_NAVIGATION,
} from '../components/AppShell';
import { lumenGridModernisationJourney } from '../fixtures/modernisation/scenarios';
import {
  lumenGridCurrentValueStreamOutputAwaitingReview,
  lumenGridPriorityValueStream,
} from '../fixtures/modernisation/valueStreamMapping';
import {
  useDiscoveryPlan,
  type ActivityOutputVersion,
  type DiscoveryPlan,
  type DurableActivityOutput,
  type DurableDiscoveryActivity,
} from '../hooks/useDiscoveryPlan';
import { useEngagement } from '../hooks/useEngagement';
import { useEngagementSetup } from '../hooks/useEngagementSetup';
import type { ActivityInstance } from '../fixtures/modernisation/activities';
import type { UseSessionReturn } from '../hooks/sessionTypes';
import type { StreamWorkspaceStream } from '../components/streamMappingWorkspaceTypes';
import StreamCurrentStateVsmPanel from '../components/StreamCurrentStateVsmPanel';
import { ActivityOutputReview, type ActivityOutputReviewItem } from './ActivityOutputReview';
import { DiscoveryActivityDetail } from './DiscoveryActivityDetail';
import { DiscoveryPhaseWorkspace } from './DiscoveryPhaseWorkspace';
import {
  CurrentStateMappingInitiation,
  emptyCurrentStateMappingSetup,
} from './CurrentStateMappingInitiation';
import { EngagementAttentionOverview } from './EngagementAttentionOverview';
import { GoalOutcomeMeasureOverview } from './GoalOutcomeMeasureOverview';
import { FindingSynthesis } from './FindingSynthesis';
import {
  EngagementActiveOverview,
  EngagementActivationRecovery,
  EngagementActivityWorkspace,
  EngagementActivities,
  EngagementClientVisionWorkspace,
  EngagementDecisions,
  EngagementEvidenceAndFindings,
  EngagementManage,
  EngagementSetupWorkspace,
  EngagementValueStreamMappingWorkspace,
} from './EngagementSetupWorkspace';
import {
  EngagementBriefWorkspace,
  EngagementActivityNotesWorkspace,
  EngagementObjectivesWorkspace,
} from './BriefFirstWorkspaces';
import { EngagementBusinessMapWorkspace } from './BusinessMapWorkspace';
import { engagementActivityNavigation } from './engagementActivityNavigation';
import { PlatformArchitectureWorkspace } from './PlatformArchitectureWorkspace';
import './engagementCoordination.css';
import './engagementJourney.css';

// 090-engagement-knowledge-tree: the Files view renders Markdown through
// react-markdown, which ships as ESM only. Loading it lazily keeps it off the
// journey's start-up path (and out of every suite that never opens Files).
const KnowledgeFilesView = lazy(() => import('../components/KnowledgeFilesView'));

function BusinessMapActivityLink({ activityId, method }: { activityId: string; method?: string }) {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set('originActivity', activityId);
  if (method) next.set('method', method);
  if (next.get('view') === 'landscape') next.set('view', 'connections');
  return <Navigate replace to={`/engagement/business-map?${next}`} />;
}

export const priorityValueStreamActivityPath = '/engagement/discovery/activities/map-priority-value-streams';
export const priorityValueStreamOutputPath = `${priorityValueStreamActivityPath}/outputs/current-value-stream-model/versions/latest`;

const standaloneDiscoveryPlan: DiscoveryPlan = {
  version: 0,
  scopeDecisions: [{
    id: 'decision-technical-estate',
    state: 'proposed',
    definitionId: 'analyse-technical-estate',
  }],
  activities: lumenGridModernisationJourney.activities.map((activity) => ({
    id: activity.id,
    sourceDecisionId: activity.state === 'proposed' ? 'decision-technical-estate' : undefined,
    definition: {
      id: activity.definition.id,
      name: activity.definition.name,
      method: activity.definition.method,
      purpose: activity.definition.purpose,
    },
    owner: { name: activity.owner.name },
    expectedOutput: { name: activity.output.name },
    rationale: activity.progress,
    state: activity.state,
  })),
};

function DeferredDestination({ title, explanation }: { title: string; explanation: string }): JSX.Element {
  return (
    <div className="journey-page">
      <header className="journey-page__header">
        <span className="journey-eyebrow">Not available here</span>
        <h1>{title}</h1>
        <p>{explanation}</p>
      </header>
    </div>
  );
}

function outputReviewItem(
  activityId: string,
  output: DurableActivityOutput,
  version: ActivityOutputVersion,
): ActivityOutputReviewItem {
  return {
    id: output.id,
    activityId,
    name: output.name,
    method: output.method,
    state: version.state,
    revision: version.version,
    returnedAt: version.returnedAt,
    reviewer: output.reviewer,
    contributors: output.contributors,
    sessionIds: [version.sessionId],
    summary: version.snapshot.payload.summary || 'Not captured',
    assumptions: version.snapshot.payload.assumptions ?? [],
    openQuestions: version.snapshot.payload.openQuestions ?? [],
    flowText: version.snapshot.payload.flowText,
  };
}

function person(value: unknown, fallbackId: string, fallbackRole: string): ActivityInstance['owner'] {
  if (typeof value === 'string') return { id: fallbackId, name: value, role: fallbackRole };
  const candidate = value as { id?: string; name?: string; role?: string } | undefined;
  return {
    id: candidate?.id || fallbackId,
    name: candidate?.name || 'Not captured',
    role: candidate?.role || fallbackRole,
  };
}

function activityInstance(activity: DurableDiscoveryActivity): ActivityInstance {
  const owner = person(activity.owner ?? activity.reviewer, `${activity.id}-owner`, 'Reviewer');
  const participants = Array.isArray(activity.participants)
    ? activity.participants.map((participant, index) => person(
      participant,
      `${activity.id}-participant-${index + 1}`,
      'Participant',
    ))
    : [];
  const expectedOutput = typeof activity.expectedOutput === 'string'
    ? { id: 'current-value-stream-model', name: activity.expectedOutput }
    : {
      id: (activity.expectedOutput as { id?: string } | undefined)?.id || 'current-value-stream-model',
      name: activity.expectedOutput?.name || 'Reviewed current-state value-stream model',
    };
  return {
    id: activity.id,
    definition: activity.definition,
    owner,
    reviewer: person(activity.reviewer ?? owner, `${activity.id}-reviewer`, 'Reviewer'),
    participants,
    output: expectedOutput,
    state: activity.state,
    progress: activity.rationale || 'Current-state mapping activity',
    question: activity.question || activity.definition.purpose,
    whyNow: activity.boundaries
      ? `${activity.boundaries.start} → ${activity.boundaries.end}`
      : activity.definition.purpose,
    inputs: [],
    completionCriteria: ['Capture at least one Flow statement and return the session output for review.'],
  };
}

function methodNativeOutput(version: ActivityOutputVersion): JSX.Element | null {
  const currentState = version.snapshot.payload.currentState;
  const stream = currentState?.stream as StreamWorkspaceStream | undefined;
  const sessionSnapshot = currentState?.session as Pick<UseSessionReturn, 'sessionId' | 'newModel' | 'spine'> | undefined;
  if (!stream || !sessionSnapshot?.newModel || !sessionSnapshot.spine) return null;
  return (
    <StreamCurrentStateVsmPanel
      stream={stream}
      session={sessionSnapshot as UseSessionReturn}
      readOnly
    />
  );
}

export function EngagementCoordinationJourney(): JSX.Element {
  const { currentEngagement, loading } = useEngagement();
  const { pathname } = useLocation();
  const scopedActivityId = pathname.match(/\/activities\/([^/]+)/)?.[1];
  const setupModel = useEngagementSetup();
  const engagementName = currentEngagement
    ? `${currentEngagement.client} · ${currentEngagement.title || currentEngagement.programme}`
    : 'Engagement';

  if (!loading && !currentEngagement) return <LegacyEngagementCoordinationJourney />;

  if (loading || setupModel.status === 'loading' || !setupModel.setup) {
    return (
      <AppShell navigation={currentEngagement?.lifecycle === 'draft'
        ? ENGAGEMENT_DRAFT_NAVIGATION
        : ENGAGEMENT_ACTIVE_NAVIGATION}
      >
        <div className="eng-setup-loading" role="status">Loading engagement...</div>
      </AppShell>
    );
  }

  // Existing engagements have no setup record by design. They keep their
  // current specialist destinations while resolving as active.
  if (setupModel.legacy) return <LegacyEngagementCoordinationJourney />;

  if (setupModel.setup.lifecycle === 'draft') {
    return (
      <AppShell navigation={ENGAGEMENT_DRAFT_NAVIGATION}>
        <EngagementSetupWorkspace model={setupModel} engagementName={engagementName} />
      </AppShell>
    );
  }

  if (setupModel.activationState === 'finishing'
    || setupModel.activationState === 'recovery-required') {
    return (
      <AppShell navigation={ENGAGEMENT_ACTIVE_NAVIGATION}>
        <EngagementActivationRecovery model={setupModel} engagementName={engagementName} />
      </AppShell>
    );
  }

  // 100 (decision 11): Activities carries the engagement's own plan, so every
  // activity — the Brief among them (decision 6) — is one click away.
  const navigation = engagementActivityNavigation(
    ENGAGEMENT_ACTIVE_NAVIGATION,
    setupModel.setup.activities,
  );

  return (
    <AppShell navigation={navigation}>
      <FacilitatorScopeProvider value={{ engagementId: currentEngagement?.id || null, activityId: scopedActivityId ? decodeURIComponent(scopedActivityId) : undefined }}>
      <Routes>
        <Route index element={<EngagementActiveOverview setup={setupModel.setup} engagementName={engagementName} />} />
        <Route
          path="brief"
          element={<EngagementBriefWorkspace model={setupModel} engagementName={engagementName} />}
        />
        <Route path="activities" element={<EngagementActivities setup={setupModel.setup} />} />
        <Route path="business-map" element={<EngagementBusinessMapWorkspace model={setupModel} />} />
        {setupModel.setup.activities.map((activity) => (
          <Fragment key={activity.id}>
            <Route
              path={`activities/${activity.id}`}
              element={<EngagementActivityWorkspace model={setupModel} activityId={activity.id} />}
            />
            <Route
              path={`activities/${activity.id}/specialist/value-stream-mapping`}
              element={(
                <EngagementValueStreamMappingWorkspace
                  model={setupModel}
                  activityId={activity.id}
                />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/client-vision`}
              element={(
                <EngagementClientVisionWorkspace
                  model={setupModel}
                  activityId={activity.id}
                />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/objectives`}
              element={(
                <EngagementObjectivesWorkspace
                  model={setupModel}
                  activityId={activity.id}
                />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/domains-stakeholders`}
              element={(
                <BusinessMapActivityLink activityId={activity.id} />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/notes`}
              element={(
                <EngagementActivityNotesWorkspace
                  model={setupModel}
                  activityId={activity.id}
                />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/platform-architecture`}
              element={(
                <PlatformArchitectureWorkspace
                  model={setupModel}
                  activityId={activity.id}
                />
              )}
            />
            <Route
              path={`activities/${activity.id}/specialist/event-storming`}
              element={(
                <BusinessMapActivityLink activityId={activity.id} method="event-storming" />
              )}
            />
          </Fragment>
        ))}
        <Route path="evidence/*" element={<EngagementEvidenceAndFindings setup={setupModel.setup} />} />
        <Route path="findings/*" element={<Navigate to="/engagement/evidence" replace />} />
        <Route path="decisions" element={<EngagementDecisions setup={setupModel.setup} />} />
        <Route
          path="files/*"
          element={(
            <Suspense fallback={<div className="journey-page" role="status">Loading files...</div>}>
              <KnowledgeFilesView />
            </Suspense>
          )}
        />
        <Route path="manage" element={<EngagementManage model={setupModel} engagementName={engagementName} />} />
        <Route path="*" element={<Navigate to="/engagement" replace />} />
      </Routes>
      </FacilitatorScopeProvider>
    </AppShell>
  );
}

function LegacyEngagementCoordinationJourney(): JSX.Element {
  const navigate = useNavigate();
  const { currentEngagement, loading: engagementLoading } = useEngagement();
  const discoveryPlan = useDiscoveryPlan(standaloneDiscoveryPlan);
  const journey = lumenGridModernisationJourney;
  const mappingActivity = journey.activities.find(
    (activity) => activity.definition.id === 'map-current-value-streams',
  );

  if (!mappingActivity) throw new Error('Canonical LumenGrid mapping activity is missing');

  const durableMappingActivity = discoveryPlan.plan?.version === 1
    ? discoveryPlan.plan.activities.find(
      (candidate) => candidate.definition.id === mappingActivity.definition.id,
    )
    : undefined;
  const activeActivityPath = durableMappingActivity
    ? `/engagement/discovery/activities/${encodeURIComponent(durableMappingActivity.id)}`
    : priorityValueStreamActivityPath;
  const activeOutputPath = `${activeActivityPath}/outputs/current-value-stream-model/versions/latest`;
  const workspaceQuery = new URLSearchParams({
    view: 'vsm',
    activity: durableMappingActivity?.id || mappingActivity.definition.id,
    returnTo: activeActivityPath,
  });
  const streamId = durableMappingActivity?.streamId || lumenGridPriorityValueStream.id;
  const workspacePath = `/streams/${encodeURIComponent(streamId)}/workspace?${workspaceQuery}`;
  const activity = durableMappingActivity
    ? activityInstance(durableMappingActivity)
    : currentEngagement
      ? null
      : mappingActivity;
  const currentOutput = durableMappingActivity?.outputs?.find(
    (output) => output.id === mappingActivity.output.id,
  );
  const latestVersion = currentOutput?.versions.at(-1);
  const reviewOutput = currentOutput && latestVersion
    ? outputReviewItem(durableMappingActivity?.id ?? mappingActivity.id, currentOutput, latestVersion)
    : null;
  const versionHistory = currentOutput
    ? currentOutput.versions.map((version) => outputReviewItem(
      durableMappingActivity?.id ?? mappingActivity.id,
      currentOutput,
      version,
    ))
    : [];

  if (engagementLoading) {
    return (
      <AppShell navigation={MODERNISATION_NAVIGATION}>
        <div className="journey-page">
          <section className="journey-panel" aria-live="polite">
            <div className="journey-empty-state" role="status">Loading engagement…</div>
          </section>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell navigation={MODERNISATION_NAVIGATION}>
      <Routes>
        <Route
          index
          element={(
            <EngagementAttentionOverview
              journey={journey}
              activityPath={priorityValueStreamActivityPath}
              engagement={currentEngagement}
              discoveryPlan={discoveryPlan.plan}
              loading={discoveryPlan.loading}
            />
          )}
        />
        <Route path="direction" element={<GoalOutcomeMeasureOverview journey={journey} engagement={currentEngagement} />} />
        <Route path="goals" element={<Navigate to="/engagement/direction" replace />} />
        <Route
          path="discovery"
          element={(
            <DiscoveryPhaseWorkspace
              journey={journey}
              activityPath={activeActivityPath}
              engagementName={currentEngagement?.client}
              discoveryPlan={discoveryPlan}
            />
          )}
        />
        <Route path="activities" element={<Navigate to="/engagement/discovery" replace />} />
        <Route
          path="discovery/current-state-mapping/start"
          element={(
            <CurrentStateMappingInitiation
              status={discoveryPlan.currentStateMappingStatus || 'idle'}
              error={discoveryPlan.currentStateMappingError}
              initialValues={discoveryPlan.pendingCurrentStateMapping || emptyCurrentStateMappingSetup}
              onCancel={() => navigate('/engagement/discovery')}
              onSubmit={async (values) => {
                const result = await discoveryPlan.startCurrentStateMapping(values);
                if (!result) return;
                const returnTo = `/engagement/discovery/activities/${encodeURIComponent(result.activityId)}`;
                const query = new URLSearchParams({
                  view: 'vsm', activity: result.activityId, returnTo,
                });
                navigate(`/streams/${encodeURIComponent(result.streamId)}/workspace?${query}`);
              }}
            />
          )}
        />
        <Route
          path="discovery/activities/:activityId"
          element={activity ? (
            <DiscoveryActivityDetail
              activity={activity}
              workspacePath={workspacePath}
              outputPath={activeOutputPath}
            />
          ) : (
            <Navigate to="/engagement/discovery" replace />
          )}
        />
        <Route
          path="discovery/activities/:activityId/outputs/current-value-stream-model/versions/latest"
          element={reviewOutput && currentOutput && latestVersion ? (
            <ActivityOutputReview
              output={reviewOutput}
              versions={versionHistory}
              methodNativeOutput={methodNativeOutput(latestVersion)}
              activityPath={activeActivityPath}
              activityName={activity?.definition.name ?? currentOutput.name}
              busy={discoveryPlan.pendingActivityCommand !== null}
              error={discoveryPlan.activityOutputError?.message}
              conflict={discoveryPlan.activityOutputError?.conflict}
              onLoadLatest={discoveryPlan.loadLatestPlan}
              onCorrectAtSource={async () => {
                const saved = await discoveryPlan.requestActivityOutputChanges(
                  durableMappingActivity?.id ?? mappingActivity.id,
                  currentOutput.id,
                  latestVersion.version,
                  currentOutput.reviewer.id,
                  new Date().toISOString(),
                );
                if (saved) navigate(workspacePath);
              }}
              onMarkReviewed={async () => {
                await discoveryPlan.reviewActivityOutput(
                  durableMappingActivity?.id ?? mappingActivity.id,
                  currentOutput.id,
                  latestVersion.version,
                  currentOutput.reviewer.id,
                  new Date().toISOString(),
                );
              }}
            />
          ) : discoveryPlan.loading ? (
            <div className="journey-page"><p>Loading activity output…</p></div>
          ) : !currentEngagement && discoveryPlan.plan?.version === 0 ? (
            <ActivityOutputReview
              output={lumenGridCurrentValueStreamOutputAwaitingReview}
              versions={[lumenGridCurrentValueStreamOutputAwaitingReview]}
              onCorrectAtSource={() => navigate(workspacePath)}
              onMarkReviewed={() => undefined}
            />
          ) : (
            <Navigate to={activeActivityPath} replace />
          )}
        />
        <Route
          path="findings/*"
          element={(
            <FindingSynthesis
              discoveryPlan={discoveryPlan.plan}
              discoveryRevision={discoveryPlan.revision}
              onDiscoveryCommitted={discoveryPlan.loadLatestPlan}
            />
          )}
        />
        <Route path="programme" element={<DeferredDestination title="Modernisation plan" explanation="Interventions, calibration and ROI are not part of engagement setup." />} />
        <Route path="*" element={<Navigate to="/engagement" replace />} />
      </Routes>
    </AppShell>
  );
}
